import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import sortBy from 'lodash/sortBy';
import {
  ComplexListData,
  FireflyBaseSuggesterInputs,
  groupBy,
  GroupedData,
  NestedValuePipe,
  ChipSize
} from '@capital-access/firefly/components';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../../common-components.const';
import { FilterLogicType, AND, OR } from '@capital-access/common/utils';
import { HashMap, LocalizationService } from '@capital-access/common/localization';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ca-multiple-master-suggester',
  templateUrl: './multiple-master-suggester.component.html',
  styleUrls: ['./multiple-master-suggester.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class MultipleMasterSuggesterComponent extends FireflyBaseSuggesterInputs implements OnChanges {
  @Input() override items: ComplexListData[] = [];
  @Input() selectedItems: ComplexListData[] = [];
  @Input() selectedItemsCategoryPath = '';
  @Input() masterSuggesterItemTemplate!: TemplateRef<unknown>;
  @Input() selectedItemDeleteBtnTemplate!: TemplateRef<unknown> | null;
  @Input() selectedItemTemplate!: TemplateRef<unknown>;
  @Input() selectedItemsGroupHeaderTemplate!: TemplateRef<unknown>;
  @Input() customSelectedItemTemplate!: TemplateRef<unknown> | null;
  @Input() selectedItemsListClass: string | string[] = 'mt-3';
  @Input() selectedItemRootClass!: string | string[];
  @Input() actionsForCategories: boolean = true;
  @Input() actionsForAllList: boolean = true;
  @Input() maxSelectedItemsCount!: number | null;
  @Input() popoverPlacement = 'bottom-left top-left';
  @Input() popoverAutoClose: boolean | string = 'outside';
  @Input() popoverContainer = '';
  @Input() heading!: string;
  @Input() showFilterLogic: boolean = false;
  @Input() filterLogic: FilterLogicType = AND;

  @Output() remove = new EventEmitter();
  @Output() removeMany = new EventEmitter();
  @Output() selectMany = new EventEmitter();
  @Output() itemClick = new EventEmitter<ComplexListData>();
  @Output() filterLogicChange = new EventEmitter<FilterLogicType>();

  groupedSelectedData!: GroupedData;

  // Chip group properties
  ChipSize = ChipSize;
  selectedSource: FilterLogicType = this.filterLogic;
  localizationScope = COMMON_COMPONENTS_LOCALIZATION_SCOPE;

  private nestedValuePipe = inject(NestedValuePipe);
  private readonly localizationService = inject(LocalizationService);
  protected readonly localization = toSignal(this.localizationService.getLocalization(this.localizationScope), {
    initialValue: {} as HashMap
  });

  readonly filterLogicTabs = computed(() => [
    {
      name: this.localization()['customFieldAnd'] || 'And',
      key: AND as FilterLogicType
    },
    {
      name: this.localization()['customFieldOr'] || 'Or',
      key: OR as FilterLogicType
    }
  ]);

  get hasSelectedItemDeleteBtnTemplate() {
    return this.selectedItemDeleteBtnTemplate instanceof TemplateRef;
  }

  get hasGroupedSelectedData() {
    return (
      this.groupedSelectedData &&
      this.groupedSelectedData.groupedItems[0] &&
      this.groupedSelectedData.groupedItems[0].value.length
    );
  }

  get shouldShowFilterLogic() {
    return this.showFilterLogic && this.selectedItems && this.selectedItems.length > 1;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedItems?.currentValue || changes.selectedItemsCategoryPath?.currentValue) {
      this.groupSelectedItems();
    }

    if (changes.filterLogic && changes.filterLogic.currentValue) {
      this.selectedSource = changes.filterLogic.currentValue;
    }
  }

  onSelect(event: unknown) {
    const item = event as ComplexListData;
    this.selectedItems.push(item);
    this.select.emit(item);
    this.groupSelectedItems();
  }

  onRemove(event: unknown) {
    const item = event as ComplexListData;
    this.selectedItems = this.selectedItems.filter(i => i[this.idField] != item[this.idField]);
    this.remove.emit(item);
    this.groupSelectedItems();
  }

  onSelectMany(event: unknown[]) {
    const items = event as ComplexListData[];
    this.selectedItems = [...this.selectedItems, ...items];
    this.selectMany.emit(items);
    this.groupSelectedItems();
  }

  onRemoveMany(event: unknown) {
    const items = event as ComplexListData[];
    this.selectedItems = this.selectedItems.filter(i => {
      return items.every(item => i[this.idField] !== item[this.idField]);
    });
    this.removeMany.emit(event);
    this.groupSelectedItems();
  }

  onChipSelect(event: Record<string, unknown>) {
    const key = event['key'];
    if (key === AND || key === OR) {
      this.selectedSource = key;
      this.filterLogicChange.emit(this.selectedSource);
    }
  }

  private groupSelectedItems() {
    this.groupedSelectedData = groupBy(
      this.selectedItems,
      this.selectedItemsCategoryPath,
      this.nestedValuePipe.transform
    );
    this.groupedSelectedData.groupedItems = sortBy(this.groupedSelectedData.groupedItems, 'name');
  }
}
